import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import { Accordion, AccordionTab } from 'primereact/accordion';
import { Card } from "primereact/card";
import { useContext, useEffect, useMemo, useRef, useState } from "react";
import { UserContext } from "../UserContext";
import { AcaaMap } from '../components/AcaaMap.jsx';
import { LocationSensorTable } from '../components/LocationSensorTable.jsx';
import PreferencesBar from '../components/PreferenceDropdown.jsx';
import { departmentColorCode } from '../utilities/departmentColors.js';
import { isInactiveSensor } from '../utilities/sensorActivity.js';

mapboxgl.accessToken = import.meta.env.VITE_MAPBOX_TOKEN;

const zoom = 15.4;
const fiveMin = 5 * 60;

export const LocationSensorMap = (props) => {
	const [sensorMetaData, setSensorMetaData] = useState([]);
	const [currentMarkers, setCurrentMarkers] = useState([]);
	// const [selectedPreferences, setSelectedPreferences] = useState();
	const [activeIndex, setActiveIndex] = useState(-1);
	const [inactiveOnly, setInactiveOnly] = useState(false);
	const [filterDepts, setFilterDepts] = useState();

	const user = useContext(UserContext);
	const map = useRef(null);
	const preferences = [];

	const [userPresetFilters, setUserPresetFilters] = useState([]);
	const [deptMetaData, setDeptMetaData] = useState([]);
	const [allDepts, setAllDepts] = useState([]);

	const filtersRef = useRef(userPresetFilters);
	const openMarkerRef = useRef(null);


	// ********START OF API CALL DEFINITIONS********
	const fetchAssetData = async (chosenPreferences) => {

		//I refactored this fetch data, it used to do two different fetches, one hit LocationNow and the other hit LocationSensors
		//They were returning seemingly the same data, so I created a viewmodel to return everything ONCE
		try {
			console.log(chosenPreferences);
			await user.apiCall(`LocationNow`, { preferences: chosenPreferences || userPresetFilters, searchTerm: props.globalFilterValue }, "POST")
				.then((res) => {
					const now = new Date();
					console.log("fetchAssetData: " + now.toString())
					setSensorMetaData(res.data);
					getSetUniqueDepartmentValues(res.data);
				});
		}
		catch (error) {
			console.error(error.message)
		}
	}

	useEffect(() => {
		filtersRef.filters = userPresetFilters;
	}, [userPresetFilters]);

	useEffect(() => {
		let departments = [];
		let activeOptions = [];
		let userPref = [];

		let isMounted = true; // Flag to check if component is still mounted
		const fetchInitialData = async () => {
			console.log("in initial fetch");

			try {
				userPref !== null ? await fetchAssetData(userPref) : await fetchAssetData();

			} catch (error) {
				console.error('Error fetching data:', error);
			}
			// repeat.current = setInterval(() => fetchAssetData(userPresetFilters ? userPresetFilters : departments), 5 * 40 * 1000);

		};

		const fetchMetaData = async () => {
			const res = await user.apiCall("findOptions");
			const optionArray = res.data.map(option => ({
				name: option.assetDepartmentName,
				value: option.assetDepartmentName,
				color: departmentColorCode(option.assetDepartmentName, option.assetDepartmentColor),
				default: option.assetDepartmentDefault
			}));

			optionArray.forEach(option => {
				if (option.default) {
					activeOptions.push(option);
					departments.push(option.name);
				}
			});

			getUserPreferences();
			setDeptMetaData(activeOptions);
			setAllDepts(departments);

		}

		const getUserPreferences = async () => {
			console.log(user.userInfo);
			const preferencesRes = await user.apiCall(`selectedPreferences/${user.userInfo.email}`);
			console.log(preferencesRes);
			if (preferencesRes?.data?.preferences !== null && preferencesRes?.data?.preferences !== undefined) {
				userPref = JSON.parse(preferencesRes?.data?.preferences);
				userPref = userPref.filter(pref => departments.includes(pref));
				setUserPresetFilters(userPref);
			}

			fetchInitialData();

		}
		if (user.userInfo !== null && user.userInfo !== undefined) {
			console.log(user.userInfo);
			fetchMetaData();

		}



		// Clean up function
		// return () => {
		// 	isMounted = false; // Component is unmounting
		// 	if (repeat.current) {
		// 		clearInterval(repeat.current);
		// 	}
		// };
	}, []);

	const visibleSensors = useMemo(
		() => inactiveOnly ? (sensorMetaData || []).filter(isInactiveSensor) : sensorMetaData,
		[sensorMetaData, inactiveOnly]
	);

	//this is used to set the markers on the map, need to do this everytime the sensor data updates (every 60 seconds)
	useEffect(() => {
		if (Array.isArray(sensorMetaData) && sensorMetaData.length > 0) {
			const serialList = (visibleSensors || [])
				.map((ftr) => ftr?.properties?.assetSerial)
				.filter(Boolean);
			assembleMarkers(serialList);
		}
		// if (repeat.current) {
		// 	clearInterval(repeat.current)
		// 	repeat.current = setInterval(() =>
		// 		fetchAssetData(userPresetFilters), 40 * 1000);
		// }
	}, [sensorMetaData, visibleSensors]);

	useEffect(() => {
		let intervalId = setInterval(() => { fetchAssetData(filtersRef.filters) }, 60 * 1000);
		return () => { console.log("in clear:"); clearInterval(intervalId) }
	}, [])

	useEffect(() => {
		const onPointerDown = (event) => {
			const open = openMarkerRef.current;
			if (!open?.hideAssetPopup) {
				return;
			}
			const target = event.target;
			const popupEl = open.getPopup?.()?.getElement?.();
			const markerEl = open.getElement?.();
			if (popupEl?.contains(target) || markerEl?.contains(target)) {
				return;
			}
			open.hideAssetPopup();
		};
		document.addEventListener('pointerdown', onPointerDown);
		return () => document.removeEventListener('pointerdown', onPointerDown);
	}, []);


	const assembleMarkers = (assetSerialList) => {
		//need the meta data to assemble the markers, because it has the colors
		try {
			openMarkerRef.current = null;
			for (let i = currentMarkers.length - 1; i >= 0; i--) {
				currentMarkers[i].remove();
				currentMarkers.pop();
			}
			if (Array.isArray(sensorMetaData) && sensorMetaData.length > 0 && map.current) {


				for (const feature of sensorMetaData) {
					if (!feature?.properties) {
						continue;
					}
					const assetSerial = feature.properties.assetSerial;
					if (!assetSerial) {
						continue;
					}
					if (!assetSerialList.find((val) => val === assetSerial)) {
						continue;
					}
					// create a HTML element for each feature
					const el = document.createElement('div');
					const elRect = document.createElement('div');
					const elTri = document.createElement('div');
					el.id = 'marker' + assetSerial;
					let rectClass = "acaa-marker"

					if (isInactiveSensor(feature)) {
						rectClass += " out-of-range";
					}
					var deptObj = (deptMetaData || []).filter(obj => {
						return obj.value === feature.properties.department
					})

					elRect.className = rectClass;
					const deptColor = '#' + departmentColorCode(feature.properties.department, deptObj[0]?.color);
					elRect.style.background = deptColor
					el.appendChild(elRect);

					elTri.className = 'triangle-down ';
					el.appendChild(elTri);

					// make a marker for each feature and add it to the map
					const popup = new mapboxgl.Popup({
						closeButton: false,
						closeOnClick: false,
						offset: 25
					}).setHTML(assemblePopup(feature.properties, false));
					let imageReady = false;
					const loadPopupImage = () => {
						if (imageReady) {
							return;
						}
						popup.setHTML(assemblePopup(feature.properties, true));
						imageReady = true;
					};
					const marker = new mapboxgl.Marker(el)
						.setLngLat(feature.geometry.coordinates)
						.setPopup(popup)
						.addTo(map.current);
					marker.showAssetPopup = () => {
						const previous = openMarkerRef.current;
						if (previous && previous !== marker) {
							const previousPopup = previous.getPopup?.();
							if (previousPopup?.isOpen()) {
								previous.togglePopup();
							}
						}
						loadPopupImage();
						if (!popup.isOpen()) {
							marker.togglePopup();
						}
						openMarkerRef.current = marker;
					};
					marker.hideAssetPopup = () => {
						if (popup.isOpen()) {
							marker.togglePopup();
						}
						if (openMarkerRef.current === marker) {
							openMarkerRef.current = null;
						}
					};

					currentMarkers.push(marker)
					const markerDiv = marker.getElement();
					const hoverCapable = window.matchMedia('(hover: hover) and (pointer: fine)').matches;
					if (hoverCapable) {
						markerDiv.addEventListener('mouseenter', () => marker.showAssetPopup());
						markerDiv.addEventListener('mouseleave', () => marker.hideAssetPopup());
						markerDiv.addEventListener('click', (e) => e.stopPropagation());
					} else {
						markerDiv.addEventListener('click', (e) => {
							e.stopPropagation();
							if (popup.isOpen()) {
								marker.hideAssetPopup();
							} else {
								marker.showAssetPopup();
							}
						});
					}
				}
			}

		}
		catch (exception) {
			console.error(exception)
		}
	}
	const updatePreferences = async (preferences) => {
		console.log(preferences);
		setUserPresetFilters(preferences);
		fetchAssetData(preferences)
		let reqBody = { user: user.userInfo.email, preferences: preferences }
		await user.apiCall("selectedPreferences", reqBody, "POST")
	}
	const escapeHtml = (value) => String(value ?? '')
		.replace(/&/g, '&amp;')
		.replace(/</g, '&lt;')
		.replace(/>/g, '&gt;')
		.replace(/"/g, '&quot;');

	const assemblePopup = (data, includeImage) => {
		const hasEventTime = data?.eventDateTime;
		const timeStr = hasEventTime ? diffTimeAgo(data.eventDateTime) : '0 mins';
		const hourCheck = isInactiveSensor(data) ? "old" : "";
		const deptVal = data?.department ?? '';
		var deptObj = (deptMetaData || []).filter(obj => {
			return obj.value === deptVal
		})
		var bgColor = '#' + departmentColorCode(deptVal, deptObj[0]?.color);
		const deptClass = deptVal ? deptVal.toLowerCase() : '';
		const makeAndModel = data?.makeAndModel ?? 'Unknown';
		const imageURL = data?.imageURL ?? '';
		const assetSerial = data?.assetSerial ?? 'Unknown';
		const photo = includeImage && imageURL
			? `<img alt="Asset image" src="${escapeHtml(imageURL)}" />`
			: `<div class="asset-photo-placeholder" aria-hidden="true"></div>`;
		return `
				<div class="${hourCheck}">
					<div class="visual">
						<div style="background-color: ${escapeHtml(bgColor)}" class="name ${escapeHtml(deptClass)}" >
							${escapeHtml(makeAndModel)}
						</div>
						${photo}
					</div>
					<div class="data">
						<div class="time">
							<i class="pi pi-eye"></i><span>${escapeHtml(timeStr)} ago</span>
						</div>
						<div class="asset-label">${escapeHtml(assetSerial)}</div>
					</div>
				</div>`
	}

	const diffTimeAgo = (feature) => {
		let edt = new Date(feature);
		let now = new Date();
		let dMs = now - edt;

		const minsDiff = Math.floor(dMs / (60 * 1000)) % 60;
		const hrsDiff = Math.floor(dMs / (60 * 60 * 1000));

		let toReturn = "";
		if (hrsDiff > 0) {
			toReturn += `${hrsDiff} hr`;
			toReturn += hrsDiff > 1 ? "s " : " ";
		}
		toReturn += `${minsDiff} min`;
		if (minsDiff > 1) {
			toReturn += "s";
		}

		return toReturn;
	}

	const handleTableFilter = (fltrData) => {
		const serialList = (fltrData || [])
			.map((item) => item?.properties?.assetSerial)
			.filter(Boolean);
		getSetUniqueDepartmentValues(fltrData || []);
		assembleMarkers(serialList);
	}

	const getSetUniqueDepartmentValues = (fltrData) => {
		const allDepartments = (fltrData || [])
			.map((item) => item?.properties?.department)
			.filter(Boolean);
		const uniqueDepartments = [...new Set(allDepartments)];
		setFilterDepts(uniqueDepartments);
	}

	const centerOn = (coords, id) => {
		map.current.flyTo({ center: coords, zoom: 16.4 });
		var marker = currentMarkers.filter(obj => {
			return obj._element && obj._element.id === id
		})
		if (marker[0]) {
			marker[0].showAssetPopup?.();
			setTimeout(function () {
				marker[0]?.hideAssetPopup?.();
			}, 3000);
		}

	}

	const togglePane = () => {
		if (activeIndex === 0) {
			setActiveIndex(-1);
		} else if (activeIndex === -1) {
			setActiveIndex(0);
		}
	};

	return (
		<span id="LocSensMap">
			<AcaaMap mapRef={map}
				startZoom={zoom}
				showJumpers
			>
				<PreferencesBar selectedPreferences={userPresetFilters}
					updatePreferences={updatePreferences}
					preferenceOptions={deptMetaData} />
			</AcaaMap>
			{/* User Guide; removed for now, needs to be updated if added back */}
			{/* <a href="https://stacaaintegrations.blob.core.windows.net/user-guides/FindUserGuide.pdf" download="RolodexUserGuide.pdf" target="_blank" rel="noreferrer">
				<i className="map-overlay-help pi pi-question-circle"></i>
			</a> */}
			<div className="map-overlay">

				<Card className='filter-style'>
					<Accordion activeIndex={activeIndex} onTabChange={(e) => setActiveIndex(e.index)}>
						<AccordionTab header="Asset Detail">
							<div className="loc-table">
								<LocationSensorTable data={visibleSensors}
									centerFunc={centerOn}
									handleFilter={handleTableFilter}
									deptList={(userPresetFilters?.length == 0 ? allDepts : userPresetFilters)}
									deptInfo={deptMetaData}
									handleAssetClick={togglePane}
									setGlobalFilterValue={props.setGlobalFilterValue}
									globalFilterValue={props.globalFilterValue}
									departmentFilters={filterDepts}
									inactiveOnly={inactiveOnly}
									onInactiveOnlyChange={setInactiveOnly}
								/>
							</div>
						</AccordionTab>
					</Accordion>
				</Card>
			</div>
		</span>
	);
};
